import React, { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Modal } from '../../common/Modal';
import { Select } from '../../common/LeboncoinSelect';
import { toast } from 'react-toastify';
import { useRequestManager } from '../../common/RequestManager';
import {
  CreateExternalListingModal_CreateExternalListingMutation,
  CreateExternalListingModal_CreateExternalListingMutationVariables,
  CreateExternalListingModal_GetShopsQuery,
  CreateExternalListingModal_GetShopsQueryVariables,
  CreateAdExternalListingInput,
  ListingsSection_AdFragment,
} from '../../../../codegen/graphql';

export const GET_SHOPS_QUERY = gql`
  query CreateExternalListingModal_GetShops {
    shops {
      id
      shopType
      shopKey
      title
    }
  }
`;

export const CREATE_EXTERNAL_LISTING_MUTATION = gql`
  mutation CreateExternalListingModal_CreateExternalListing($input: CreateAdExternalListingInput!) {
    createAdExternalListing(input: $input) {
      id
      shopId
      shop {
        shopType
      }
      lang
    }
  }
`;

export const LANGS_BY_SHOP_TYPE: Record<string, { identifier: string; label: string }[]> = {
  leboncoin: [{ identifier: 'FR', label: 'France' }],
  ebay: [
    { identifier: 'FR', label: 'France' },
    { identifier: 'DE', label: 'Germany' },
    { identifier: 'IT', label: 'Italy' },
    { identifier: 'ES', label: 'Spain' },
    { identifier: 'NL', label: 'Netherlands' },
    { identifier: 'PT', label: 'Portugal' },
    { identifier: 'IE', label: 'Ireland' },
    { identifier: 'AT', label: 'Austria' },
    { identifier: 'BE', label: 'Belgium' },
  ],
  temu: [
    { identifier: 'FR', label: 'France' },
    { identifier: 'ES', label: 'Spain' },
    { identifier: 'IT', label: 'Italy' },
    { identifier: 'DE', label: 'Germany' },
    { identifier: 'NL', label: 'Netherlands' },
    { identifier: 'PT', label: 'Portugal' },
    { identifier: 'IE', label: 'Ireland' },
    { identifier: 'AT', label: 'Austria' },
    { identifier: 'BE', label: 'Belgium' },
  ],
  aliexpress: [
    { identifier: 'FR', label: 'France' },
    { identifier: 'ES', label: 'Spain' },
    { identifier: 'IT', label: 'Italy' },
    { identifier: 'DE', label: 'Germany' },
    { identifier: 'NL', label: 'Netherlands' },
    { identifier: 'PT', label: 'Portugal' },
    { identifier: 'IE', label: 'Ireland' },
    { identifier: 'AT', label: 'Austria' },
    { identifier: 'BE', label: 'Belgium' },
  ],
  'direct-sale': [{ identifier: 'FR', label: 'France' }],
};

interface CreateExternalListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  adData: ListingsSection_AdFragment;
}

export function CreateExternalListingModal({
  isOpen,
  onClose,
  onSuccess,
  adData,
}: CreateExternalListingModalProps) {
  const { trackRequest } = useRequestManager();

  const { data: shopsData } = useQuery<
    CreateExternalListingModal_GetShopsQuery,
    CreateExternalListingModal_GetShopsQueryVariables
  >(GET_SHOPS_QUERY, {
    skip: !isOpen,
  });

  const [createExternalListing] = useMutation<
    CreateExternalListingModal_CreateExternalListingMutation,
    CreateExternalListingModal_CreateExternalListingMutationVariables
  >(CREATE_EXTERNAL_LISTING_MUTATION);

  const [shopId, setShopId] = useState('');
  const [lang, setLang] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const shops = shopsData?.shops ?? [];

  const usedShopLangCombos = useMemo(() => {
    return new Set(
      adData.externalListings.map((listing) => (
        `${listing.shopId}:${listing.lang.toUpperCase()}`
      )),
    );
  }, [adData.externalListings]);

  const shopOptions = useMemo(() => {
    return shops.map((shop) => ({
      identifier: shop.id,
      label: `${shop.title} (${shop.shopType})`,
    }));
  }, [shops]);

  const selectedShop = useMemo(() => {
    return shops.find((shop) => shop.id === shopId) ?? null;
  }, [shops, shopId]);

  const langOptions = useMemo(() => {
    if (!selectedShop) {
      return [];
    }

    const langs = LANGS_BY_SHOP_TYPE[selectedShop.shopType] ?? [{ identifier: 'FR', label: 'France' }];

    return langs;
    // Disabled
    // return langs.filter((langOption) => {
    //   return !usedShopLangCombos.has(`${shopId}:${langOption.identifier}`);
    // });
  }, [selectedShop, shopId, usedShopLangCombos]);

  const handleShopChange = (value: string) => {
    setShopId(value);
    setLang('');
    setErrors((prev) => ({ ...prev, shopId: '', lang: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!shopId) {
      newErrors.shopId = 'Shop is required';
    }

    if (!lang) {
      newErrors.lang = 'Language is required';
    }
    // Disabled
    // else if (usedShopLangCombos.has(`${shopId}:${lang.toUpperCase()}`)) {
    //   newErrors.lang = 'A listing already exists for this shop and language';
    // }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setShopId('');
    setLang('');
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const input: CreateAdExternalListingInput = {
        adId: adData.id,
        shopId,
        lang,
      };

      await trackRequest(
        createExternalListing({ variables: { input } }),
        {
          title: 'Creating external listing',
          isBlocking: true,
        },
      );

      toast.success('External listing created successfully');
      onSuccess();
      resetForm();
    } catch (error) {
      console.error('Error creating external listing:', error);
      toast.error('Failed to create external listing');
    }
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Add External Listing"
      onClose={handleClose}
      width="medium"
      actions={[
        {
          children: 'Cancel',
          preset: 'white',
          onClick: handleClose,
        },
        {
          children: 'Create Listing',
          preset: 'blue',
          onClick: handleSubmit,
        },
      ]}
    >
      <div className="space-y-6">
        <Select
          label="Shop"
          options={shopOptions}
          value={shopId}
          onChange={handleShopChange}
          error={errors.shopId}
          required
        />

        <Select
          label="Language"
          options={langOptions}
          value={lang}
          onChange={(value) => {
            setLang(value);
            if (errors.lang) {
              setErrors((prev) => ({ ...prev, lang: '' }));
            }
          }}
          error={errors.lang}
          required
          helperText={
            shopId && langOptions.length === 0
              ? 'All languages are already used for this shop'
              : undefined
          }
        />
      </div>
    </Modal>
  );
}
